import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { borderRadius } from '@atlaskit/theme';
import { N100 } from '@atlaskit/theme/colors';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import DropdownMenu, {
  CustomTriggerProps,
  DropdownItem,
  DropdownItemGroup,
  DropdownMenuProps,
  OnOpenChangeArgs,
} from '@databases/atlaskit/DropdownMenu';
import { SelectableButton } from '@databases/atlaskit/SelectableButton';
import { useDatabaseContent } from '@databases/database-state';
import { currentHostFrameContext } from '@databases/database-state/state/host-frame-context';
import { useDialog } from '@databases/dialog';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import { pasteContentExperience } from '@databases/frontend-metrics';
import { doProcessPastedContent } from '@databases/import/doProcessPastedContent';
import { getPasteMessageByType, PasteMessageType } from '@databases/import/messageHelper';
import { messages as importMessages } from '@databases/import/messages';
import { usePasteListener } from '@databases/import/usePasteListener';
import { messages } from '@databases/intl/messages';
import { REDO_SHORTCUT, UNDO_SHORTCUT } from '@databases/keyboard-shortcuts/shortcuts';
import { useMentionNotification } from '@databases/mention-notification';
import usePanel from '@databases/panel/usePanel';
import { useComponent, useKeyboardShortcut } from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { useScope } from '@databases/scope';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import { DatabaseChangeType } from '@databases/types/redux';
import { DatabaseBridgeAPI, getBridgeAPI, Item } from '@databases/utils/database/bridge';
import { showErrorSnackbar } from '@databases/utils/errors';
import { useCollabManager } from '@databases/utils/hooks/useCollabManager';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { isStandalone } from '@databases/utils/utils';
import useView from '@databases/views/useView';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAsync } from 'react-async';
import { useIntl } from 'react-intl-next';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import styled from 'styled-components';
import { useSnapshot } from 'valtio';

import { getIdentity } from '@canvas/connection/identity';

import { CONFLUENCE_SUPPORT_URL } from '../../../constants';
import { isApple } from '../../utils';

export type DatabaseActionKey =
  | 'open'
  | 'lock'
  | 'import'
  | 'print'
  | 'help'
  | 'copy'
  | 'feedback';

const ToggleWrapper = styled.div`
  margin-left: -10px;
  margin-right: -10px;
`;

const Shortcut = styled.div`
  margin-left: -10px;
  background-color: ${token('color.background.neutral')};
  color: ${token('color.text.subtle', N100)};
  border-radius: ${borderRadius()}px;
  padding: 4px;
  line-height: 12px;
  align-self: flex-end;
  font-size: 0.7rem;
  @media (max-width: 0px) {
    display: none;
  }
`;

const doToggleLock =
  (
    contentId: string,
    scope: string
  ): ThunkAction<void, DefaultRootState, void, AnyAction> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), { contentId });
    dispatch(
      addChangeToDraft(contentId, scope, {
        type: DatabaseChangeType.TOGGLE_LOCK_STRUCTURE,
        newValue: !database.meta.isLocked,
      })
    );
  };

export interface DatabaseActionsProps {
  contentId: string;
  isReadOnly: boolean;
  placement?: DropdownMenuProps['placement'];
  show?: DatabaseActionKey[];
}

const DatabaseActions: FunctionComponent<DatabaseActionsProps> = ({
  contentId,
  isReadOnly,
  placement,
  show,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const scope = useScope();
  const client = useAnalytics();
  const { view } = useView();
  const { showPanel } = usePanel();
  const { referencing } = useDatabaseContent();
  const { createMentionNotification } = useMentionNotification({ contentId });

  const isInlineCommentsEnabled = useBooleanFeatureFlag(
    'databases-frontend-inline-comments'
  );

  const frameContext = useSnapshot(currentHostFrameContext);
  const isExportDisabledByDSP = frameContext.data.dataSecurityPolicy?.pageExport !== true;

  const database = defaultStoreUtils.useSelector((state) =>
    selectDatabaseByContentIdRequired(state, { contentId })
  );
  const collabManager = useCollabManager(contentId);
  const undoManager = collabManager?.database.undoManager;

  const [isOpen, setIsOpen] = useState(false);
  const [showDevMenu, setShowDevMenu] = useState(false);
  const { showDialog } = useDialog();

  useEffect(() => {
    const loadDevMenu = async () => {
      const email = (await getIdentity()).email;
      if (email?.endsWith('@atlassian.com') || email?.endsWith('@usersinbuckets.com')) {
        setShowDevMenu(true);
      }
    };

    loadDevMenu();
  }, []);

  const { formatMessage } = useIntl();

  const lockAsync = useAsync({
    deferFn: useCallback(async () => {
      await dispatch(doToggleLock(contentId, scope));
    }, [contentId, dispatch, scope]),
  });
  const handleLock = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!lockAsync.isPending) {
        lockAsync.run();
      }
    },
    [lockAsync]
  );

  const handleExportClick = useCallback(() => {
    showDialog('ExportDialog');
  }, [showDialog]);

  const handleSetupClick = useCallback(() => {
    showPanel('DatabaseSetupPanel');
  }, [showPanel]);

  const handleCopyClick = useCallback(() => {
    showPanel('CopyDatabasePanel');
  }, [showPanel]);

  const handleUndo = useCallback(() => {
    undoManager?.undo();
  }, [undoManager]);
  useKeyboardShortcut(UNDO_SHORTCUT, handleUndo);

  const handleRedo = useCallback(() => {
    undoManager?.redo();
  }, [undoManager]);
  useKeyboardShortcut(REDO_SHORTCUT, handleRedo);

  const handleOpenDevTools = useCallback(() => {
    showDialog('DevtoolsDialog');
  }, [showDialog]);

  const handleOpenChange = useCallback((attrs: OnOpenChangeArgs) => {
    setIsOpen(attrs.isOpen);
  }, []);

  const trigger = useComponent(
    ({ triggerRef, ...props }: CustomTriggerProps) => (
      <SelectableButton
        testId="actions-menu.more-actions"
        iconBefore={
          <MoreIcon
            spacing="spacious"
            label={formatMessage(messages['actions-menu.more-actions'])}
          />
        }
        ref={triggerRef}
        appearance="subtle"
        $isSelected={props.isSelected}
        {...props}
      />
    ),
    [formatMessage]
  );

  const actions = useMemo(
    () => ({
      open: !show || show.includes('open'),
      lock: (!show || show.includes('lock')) && database.operations.updateMetadata,
      import: (!show || show.includes('import')) && database.operations.updateEntries,
      // used for export
      print: (!show || show.includes('print')) && !isExportDisabledByDSP,
      help: !show || show.includes('help'),
      copy: !show || show.includes('copy'),
      feedback: !show || show.includes('feedback'),
    }),
    [
      show,
      database.operations.updateMetadata,
      database.operations.updateEntries,
      isExportDisabledByDSP,
    ]
  );

  const showPasteSnackbar = useCallback(
    ({
      actionType,
      type,
      options,
    }: {
      actionType: PasteMessageType;
      type: 'warning' | 'error';
      options?: {
        showIcon?: boolean;
      };
    }) => {
      const { showIcon = true } = options || {};
      const messageId = getPasteMessageByType(actionType);

      if (type === 'warning') {
        currentSnackbarState.setMessage(formatMessage(importMessages[messageId]), {
          id: actionType,
          type: 'warning',
          actions: undoManager && [
            {
              // keeping `snackbar-warning.undo-paste` localization temporarily to reduce churn while we wait for copy updates
              content: formatMessage(importMessages['snackbar-link-message.undo-action']),
              onClick: () => {
                client.sendUndoEvent({
                  action: 'paste',
                  type: actionType as Exclude<
                    PasteMessageType,
                    'cannotReadPastedContent' | 'unexpectedDataType'
                  >,
                  contentId,
                  databaseContainer: getDatabaseContainerType(),
                });
                undoManager.undo();
                currentSnackbarState.clearMessage(actionType);
              },
            },
          ],
          showIcon,
          showCloseButton: true,
        });
      } else if (type === 'error') {
        showErrorSnackbar(actionType, formatMessage(importMessages[messageId]));
      }
    },
    [client, contentId, formatMessage, undoManager]
  );

  const handlePaste = useCallback(
    (items: DataTransferItemList) => {
      if (isReadOnly) {
        currentSnackbarState.setMessage(
          formatMessage(importMessages['snackbar-error-message.paste-on-read-only']),
          {
            showCloseButton: true,
          }
        );

        return;
      }

      pasteContentExperience.start();

      client.sendContentPastedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
      });

      const fieldLabel = formatMessage(
        importMessages['import-field-settings.field-name-inline-edit-default-text'],
        {
          fieldType: formatMessage(importMessages['import-field-settings.type-text']),
        }
      );

      dispatch(
        doProcessPastedContent(
          contentId,
          items,
          'main',
          view.id,
          fieldLabel,
          showPasteSnackbar,
          createMentionNotification
        )
      );
    },
    [
      isReadOnly,
      client,
      contentId,
      formatMessage,
      dispatch,
      view.id,
      showPasteSnackbar,
      createMentionNotification,
    ]
  );

  // TODO: move usePasteListener to `DatabaseContent`
  usePasteListener({
    onPaste: handlePaste,
  });

  const canUndo = !undoManager?.canUndo();
  const canRedo = !undoManager?.canRedo();

  const [actionsMovedToMoreMenu, setActionsMovedToMoreMenu] = useState(false);

  const [bridgeApi, setBridgeApi] = useState<any | DatabaseBridgeAPI>(null);
  useEffect(() => {
    getBridgeAPI().then(setBridgeApi);
  }, []);

  useEffect(() => {
    if (!isStandalone() || !bridgeApi) {
      return;
    }

    const undoAndRedo: Item[] = [
      {
        id: 'databases-undo',
        text: formatMessage(messages['actions-menu.undo']),
        target: '_blank',
        isDisabled: canUndo,
        shortcutText: isApple
          ? formatMessage(messages['actions-menu.mac-undo-shortcut'])
          : formatMessage(messages['actions-menu.non-mac-undo-shortcut']),
        onClick: handleUndo,
      },
      {
        id: 'databases-redo',
        text: formatMessage(messages['actions-menu.redo']),
        target: '_blank',
        isDisabled: canRedo,
        shortcutText: isApple
          ? formatMessage(messages['actions-menu.mac-redo-shortcut'])
          : formatMessage(messages['actions-menu.non-mac-redo-shortcut']),
        onClick: handleRedo,
      },
    ];

    const otherActions = [
      actions.lock && {
        id: 'databases-lock-structure',
        text: formatMessage(messages['actions-menu.lock-structure']),
        onClick: handleLock,
        showToggle: {
          isChecked: lockAsync.isPending
            ? !database.meta.isLocked
            : database.meta.isLocked,
          isDisabled: lockAsync.isPending,
        },
      },
      actions.copy && {
        id: 'databases-copy-structure',
        text: formatMessage(messages['actions-menu.copy-structure']),
        onClick: handleCopyClick,
      },
      !referencing &&
        !isReadOnly && {
          id: 'databases-templates',
          text: formatMessage(messages['actions-menu.import-and-templates']),
          onClick: handleSetupClick,
        },
      actions.print && {
        id: 'databases-export',
        text: formatMessage(messages['actions-menu.export']),
        onClick: handleExportClick,
      },
      isInlineCommentsEnabled && { id: 'view-resolved-comments' },
    ].filter(Boolean) as Item[];

    const getHelp: Item[] = [
      {
        id: 'databases-get-help',
        text: formatMessage(messages['actions-menu.get-help']),
        url: CONFLUENCE_SUPPORT_URL,
        target: '_blank',
      },
      showDevMenu && {
        id: 'databases-devtools',
        text: formatMessage(messages['actions-menu.devtools']),
        onClick: handleOpenDevTools,
      },
    ].filter(Boolean) as Item[];

    const items: Item[][] = [];

    if (undoManager) {
      items.push(undoAndRedo);
    }
    if (otherActions.length) {
      items.push(otherActions);
    }
    if (actions.help) {
      items.push(getHelp);
    }

    bridgeApi.updateMoreActions?.({
      items,
    });

    setActionsMovedToMoreMenu(true);
  }, [
    actions,
    canRedo,
    canUndo,
    database.meta.isLocked,
    formatMessage,
    handleCopyClick,
    handleExportClick,
    handleLock,
    handleOpenDevTools,
    handleRedo,
    handleSetupClick,
    handleUndo,
    isReadOnly,
    lockAsync.isPending,
    referencing,
    showDevMenu,
    undoManager,
    bridgeApi,
    isInlineCommentsEnabled,
  ]);

  const hasActions = Object.values(actions).filter((a) => a).length > 0;

  if (!hasActions || actionsMovedToMoreMenu) {
    return null;
  }

  return (
    <>
      <DropdownMenu
        trigger={trigger}
        placement={placement}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        <DropdownItemGroup>
          {undoManager && (
            <DropdownItem
              elemAfter={
                <Shortcut>
                  {isApple
                    ? formatMessage(messages['actions-menu.mac-undo-shortcut'])
                    : formatMessage(messages['actions-menu.non-mac-undo-shortcut'])}
                </Shortcut>
              }
              onClick={handleUndo}
              isDisabled={!undoManager.canUndo()}
            >
              {formatMessage(messages['actions-menu.undo'])}
            </DropdownItem>
          )}

          {undoManager && (
            <DropdownItem
              elemAfter={
                <Shortcut>
                  {isApple
                    ? formatMessage(messages['actions-menu.mac-redo-shortcut'])
                    : formatMessage(messages['actions-menu.non-mac-redo-shortcut'])}
                </Shortcut>
              }
              onClick={handleRedo}
              isDisabled={!undoManager.canRedo()}
            >
              {formatMessage(messages['actions-menu.redo'])}
            </DropdownItem>
          )}
        </DropdownItemGroup>

        <DropdownItemGroup>
          {actions.lock && (
            <Tooltip
              content={formatMessage(messages['actions-menu.lock-structure-tooltip'])}
            >
              <DropdownItem
                elemAfter={
                  <ToggleWrapper>
                    <Toggle
                      isChecked={
                        lockAsync.isPending
                          ? !database.meta.isLocked
                          : database.meta.isLocked
                      }
                      isDisabled={lockAsync.isPending}
                    />
                  </ToggleWrapper>
                }
                onClick={handleLock}
              >
                {formatMessage(messages['actions-menu.lock-structure'])}
              </DropdownItem>
            </Tooltip>
          )}
          {actions.copy && (
            <DropdownItem onClick={handleCopyClick}>
              {formatMessage(messages['actions-menu.copy-structure'])}
            </DropdownItem>
          )}

          {!referencing && (
            <DropdownItem
              testId="actions-menu.import-and-templates"
              onClick={handleSetupClick}
            >
              {formatMessage(messages['actions-menu.import-and-templates'])}
            </DropdownItem>
          )}

          {actions.print && (
            <DropdownItem onClick={handleExportClick}>
              {formatMessage(messages['actions-menu.export'])}
            </DropdownItem>
          )}
        </DropdownItemGroup>

        <DropdownItemGroup>
          {actions.help && (
            <DropdownItem
              href={CONFLUENCE_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {formatMessage(messages['actions-menu.get-help'])}
            </DropdownItem>
          )}

          {showDevMenu && (
            <DropdownItem onClick={handleOpenDevTools}>
              {formatMessage(messages['actions-menu.devtools'])}
            </DropdownItem>
          )}
        </DropdownItemGroup>
      </DropdownMenu>
    </>
  );
};

export default DatabaseActions;
